import { BALANCE, BUILDING_DEFS } from '../constants';
import type { Simulation } from '../sim';
import type { Building, Col, SimState } from '../types';
import { accessTiles } from '../world/path';

/**
 * Post-ore production. Producers run daily while functional (staffed, powered,
 * watered — updateFunctional's gates) and hard-stop otherwise: a ranch out of
 * feed produces nothing, no partial credit. Output fills the building's own
 * store (the barn) first — haulers draw from it — and only overflows onto
 * perimeter goods piles once the store is full, so ground piles mean "the
 * haulers are behind", not business as usual.
 */

/** Internal output store capacity: def override, else 4 days of production. */
export function outputCapUnits(type: keyof typeof BUILDING_DEFS): number {
  const def = BUILDING_DEFS[type];
  if (!def.produces) return 0;
  return def.outputCapUnits ?? def.produces.unitsPerDay * 4;
}

/** Where a producer may drop overflow: perimeter tiles in deterministic order. */
export function industryOutputCols(state: SimState, building: Building): Col[] {
  const def = BUILDING_DEFS[building.type];
  return [...accessTiles(state.terrain, building.x, building.y, def.w, def.h)];
}

/** Whether a producer can run today, and why not — shared by sim + inspect. */
export function productionStall(state: SimState, building: Building): string | null {
  const def = BUILDING_DEFS[building.type];
  if (!def.produces) return null;
  if (!building.functional) return 'not operating';
  if (def.consumes && building.inputUnits < def.consumes.unitsPerDay) return `no ${def.consumes.good}`;
  const out = def.produces.unitsPerDay;
  if (building.outputUnits + out <= outputCapUnits(building.type)) return null;
  for (const col of industryOutputCols(state, building)) {
    if (state.piles.has(col)) continue;
    const existing = state.goodsPiles.get(col);
    if (!existing) return null;
    if (existing.good === def.produces.good && existing.units + out <= BALANCE.goodsPileCapUnits) return null;
  }
  return 'output full';
}

/** Total goods-depot capacity: functional warehouses only. */
export function goodsDepotCapacity(state: SimState): number {
  let capacity = 0;
  for (const building of state.buildings) {
    if (!building.functional) continue;
    capacity += BUILDING_DEFS[building.type].goodsCapacityUnits ?? 0;
  }
  return capacity;
}

export function stepIndustryDaily(sim: Simulation): void {
  const state = sim.state;
  state.economy.goodsDepot.capacityUnits = goodsDepotCapacity(state);

  for (const building of state.buildings) {
    const def = BUILDING_DEFS[building.type];
    if (!def.produces) continue;
    if (productionStall(state, building) !== null) continue;
    const { good, unitsPerDay } = def.produces;
    if (def.consumes) building.inputUnits -= def.consumes.unitsPerDay;
    // Store first (all-or-nothing per day, matching the stall check), only
    // then overflow to a perimeter pile.
    if (building.outputUnits + unitsPerDay <= outputCapUnits(building.type)) {
      building.outputUnits += unitsPerDay;
      continue;
    }
    for (const col of industryOutputCols(state, building)) {
      if (state.piles.has(col)) continue;
      const existing = state.goodsPiles.get(col);
      if (existing) {
        if (existing.good !== good || existing.units + unitsPerDay > BALANCE.goodsPileCapUnits) continue;
        existing.units += unitsPerDay;
      } else {
        state.goodsPiles.set(col, { col, good, units: unitsPerDay, claimedUnits: 0, suspended: false });
      }
      break;
    }
  }
}
